import { dashboardRequest } from "../providers/aws/client";
import type { I2VLabsProviderOptions } from "../ai-settings/i2vRegistry.generated";

export type LabMediaReference = {
  bucket: string;
  key: string;
  mime_type: string;
  url?: string;
};

export type LabJobStatus =
  | "submitting"
  | "queued"
  | "running"
  | "cancel_requested"
  | "completed"
  | "failed"
  | "cancelled"
  | "submit_failed"
  | "attention_required";

export type LabProviderOptions = I2VLabsProviderOptions;

type LegacyLabProviderOptions = {
  provider_id?: undefined;
  workflow_id?: string | null;
  lora_id?: string | null;
  lora_strength?: number | null;
};

export type LabJobRequest = {
  schema_version: 1 | 2 | 3;
  job_id: string;
  mode: "image_to_video";
  provider_id: string;
  model_id?: string | null;
  prompt: string;
  negative_prompt?: string | null;
  duration_seconds: number;
  aspect_ratio?: string | null;
  resolution?: string | null;
  generate_audio: boolean;
  seed?: number | null;
  inputs: {
    first_frame: LabMediaReference;
    last_frame?: LabMediaReference | null;
    reference_images: LabMediaReference[];
  };
  created_by: { user_id: string; email?: string | null };
  registry_digest?: string | null;
  provider_options?: LabProviderOptions | LegacyLabProviderOptions;
};

export type LabAttempt = {
  schema_version?: number;
  attempt_number?: number;
  state: "prepared" | "submitting" | "accepted" | "ambiguous" | "rejected";
  provider_id?: string;
  concrete_provider_id?: string;
  task_id?: string;
  endpoint_kind?: string;
  api_version?: string;
  request_fingerprint?: string;
  attempt_history?: Array<Record<string, unknown>>;
  updated_at?: string;
  error_code?: string;
  retryable?: boolean;
};

export type LabUsageLedger = {
  schema_version: number;
  attempt_number: number;
  requested_provider_id: string;
  routing_provider_id: string;
  concrete_provider_id: string;
  model_id?: string | null;
  pricing_key?: string | null;
  pricing_version?: string | null;
  provider_task_id?: string | null;
  lifecycle_status: string;
  possible_billing: boolean;
  estimated_cost_usd?: string | null;
  provider_reported_cost_usd?: string | null;
  reconciled_cost_usd?: string | null;
  reconciliation_state: string;
  variance_reason?: string | null;
  updated_at?: string;
};

export type LabJob = {
  job_id: string;
  environment: string;
  status: LabJobStatus;
  mode: "image_to_video";
  provider_id: string;
  model_id?: string | null;
  duration_seconds: number;
  aspect_ratio?: string | null;
  resolution?: string | null;
  generate_audio: boolean;
  created_at: string;
  updated_at?: string;
  created_by: { user_id: string; email?: string | null };
  batch_job_id?: string;
  request_key: string;
  inputs: LabJobRequest["inputs"];
  request?: LabJobRequest;
  thumbnail_url?: string;
  output_url?: string;
  attempt?: LabAttempt;
  result?: {
    status: "completed" | "failed";
    provider_task_id?: string;
    concrete_provider_id?: string;
    model_id?: string;
    provider_options?: LabJobRequest["provider_options"];
    usage?: Record<string, unknown>;
    usage_ledger?: LabUsageLedger | null;
    timings?: Record<string, unknown>;
    error?: { code?: string; message?: string; retryable?: boolean };
    output?: {
      bucket: string;
      key: string;
      content_type?: string;
      size_bytes?: number;
    };
  };
};

type UploadIntent = {
  upload: { url: string; fields: Record<string, string> };
  object: LabMediaReference;
  expires_in: number;
};

export type CreateLabJobInput = {
  idempotency_key: string;
  mode: "image_to_video";
  provider_id: string;
  model_id?: string;
  prompt: string;
  negative_prompt?: string;
  duration_seconds: number;
  aspect_ratio?: string;
  resolution?: string;
  generate_audio: boolean;
  seed?: number;
  registry_digest?: string;
  provider_options?: LabProviderOptions;
  inputs: LabJobRequest["inputs"];
};

export async function uploadLabImage(
  file: File,
  role: "first_frame" | "last_frame" | "reference",
): Promise<LabMediaReference> {
  const intent = await dashboardRequest<UploadIntent>("/labs/uploads", {
    method: "POST",
    body: JSON.stringify({
      filename: file.name,
      mime_type: file.type,
      size_bytes: file.size,
      role,
    }),
  });
  const form = new FormData();
  Object.entries(intent.upload.fields).forEach(([name, value]) => {
    form.append(name, value);
  });
  form.append("file", file);
  const response = await fetch(intent.upload.url, {
    method: "POST",
    body: form,
  });
  if (!response.ok) {
    throw new Error(`Labs upload failed with ${response.status}`);
  }
  return intent.object;
}

export const createLabJob = (input: CreateLabJobInput) =>
  dashboardRequest<LabJob>("/labs/jobs", {
    method: "POST",
    body: JSON.stringify(input),
  });

export const listLabJobs = () =>
  dashboardRequest<{ data: LabJob[]; next_cursor?: string | null }>(
    "/labs/jobs?limit=50",
  );

export const getLabJob = (jobId: string) =>
  dashboardRequest<LabJob>(`/labs/jobs/${encodeURIComponent(jobId)}`);

export const cancelLabJob = (jobId: string) =>
  dashboardRequest<LabJob>(`/labs/jobs/${encodeURIComponent(jobId)}/cancel`, {
    method: "POST",
    body: "{}",
  });

export const isActiveLabStatus = (status: LabJobStatus) =>
  ["submitting", "queued", "running", "cancel_requested"].includes(status);
